import clsx from 'clsx';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import { combineRefs } from '../../../common/functions/combine-refs';
import useTestTag from '../../../common/hooks/use-test-tag';
import type { MakeRequired } from '../../../common/types/make-required';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';

export interface TextAreaProps extends MakeRequired<DynamicFieldComponentProps<string>, 'name'> {
  rows?: number;
  placeholder?: string;
  maxLength?: number;
  showCharacterCounter?: boolean;
}

export default function TextArea({
  name,
  label,
  rows,
  value,
  error,
  readOnly,
  required,
  onChange,
  onFocus,
  inputRef,
  placeholder,
  disabled,
  labelStyles,
  maxLength,
  showCharacterCounter = false,
}: TextAreaProps) {
  const [stateValue, setStateValue] = useState<string>('');

  useEffect(() => {
    setStateValue(value ?? '');
  }, [value]);

  const tagCreator = useTestTag(label || '');

  const onFocusHandler = () => onFocus?.();

  const onChangeHandler = ({ target }: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = target;

    setStateValue(value);
    onChange?.(value);
  };

  // same style as the Input component
  const inputStyles = clsx(
    'custom-scroll appearance-none leading-5 md:leading-4 rounded-sm border relative block w-full py-2 md:py-1 placeholder-gray-3 text-base md:text-sm focus:outline-hidden',
    {
      'bg-mono-1 px-2': !readOnly,
      'border-gray-4/70 cursor-default': readOnly,
      'border-error': error,
      'border-gray-4': !error && !readOnly,
      hidden: readOnly && (!stateValue || stateValue.length === 0),
      'cursor-default text-readonly': disabled,
    }
  );

  const combinedRef = combineRefs(inputRef, tagCreator('textarea'));

  return (
    <div>
      {label && name && (
        <div className="flex">
          <FieldLabel name={name} label={label} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      <textarea
        id={name}
        ref={combinedRef}
        rows={rows}
        name={name}
        className={inputStyles}
        onChange={onChangeHandler}
        onFocus={onFocusHandler}
        value={stateValue}
        readOnly={readOnly}
        spellCheck={true}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
      />
      {showCharacterCounter && maxLength && (
        <div className="text-xs text-gray-3 text-right mt-1">
          {stateValue.length}/{maxLength}
        </div>
      )}
      {readOnly && (!stateValue || stateValue.length === 0) && <EmptyFieldLabel />}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
}
